import React from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";

const QUESTIONS = [
  {
    id: "item-1",
    title: "What is Furloop Poofed?",
    content:
      "Furloop Poofed is verified, provable data erasure for refurbished devices. Every drive is wiped to international data-sanitization standards, verified by reading the drive back, and certified with a tamper-evident signature — automatically, as part of the refurb line.",
  },
  {
    id: "item-2",
    title: "How do you erase different types of drives?",
    content:
      "We pick the method that's correct for the drive: firmware sanitize (block erase) for NVMe and SATA SSDs, cryptographic erase for self-encrypting drives, and overwrite for hard drives. Machines with more than one internal drive have every drive wiped with the right method.",
  },
  {
    id: "item-3",
    title: "How do you prove the data is actually gone?",
    content:
      "After every wipe the system reads the drive's actual bytes directly from the disk (bypassing caches) to confirm the data is gone. Cryptographic erase is confirmed by the drive's own sanitize status. If a wipe can't be confirmed, the device fails and never moves forward.",
  },
  {
    id: "item-4",
    title: "Which standards do you follow?",
    content:
      "Erasure is performed and documented against NIST SP 800-88 Rev. 2 and IEEE 2883-2022. For hard-drive overwrite we offer 1-pass (NIST 800-88 Clear), 3-pass (DoD 5220.22-M), and 7-pass (DoD 5220.22-M ECE) options.",
  },
  {
    id: "item-5",
    title: "Can my customer verify the certificate themselves?",
    content:
      "Yes. Each certificate carries a unique, scannable code. The buyer of the refurbished device can scan it and independently confirm the device was erased to standard — so erasure becomes a trust signal you can put right on the resale listing.",
  },
  {
    id: "item-6",
    title: "Why not just multi-pass overwrite everything?",
    content:
      "For modern flash storage (SSD / NVMe), firmware sanitize purges every cell — including spare areas a software overwrite can't reach — so it's both faster and more thorough. Multi-pass overwrite applies primarily to traditional spinning hard drives.",
  },
  {
    id: "item-7",
    title: "Could it accidentally wipe boot or USB media?",
    content:
      "No. Removable media and USB devices are automatically protected and never touched — only internal drives are erased. The boot/eraser media itself is always safe.",
  },
  {
    id: "item-8",
    title: "Is Furloop Poofed independently certified?",
    content:
      "Today Poofed conforms to NIST SP 800-88 Rev. 2, IEEE 2883-2022, and the DoD overwrite specs, and supports requirements under GDPR, HIPAA, PCI DSS, ISO/IEC 27001, R2v3, and e-Stewards. We are not yet certified by a governing body; our first target is ADISA, with broader approvals to follow.",
  },
  {
    id: "item-9",
    title: "How does pricing work?",
    content:
      "A flat monthly subscription with enterprise volume discounts — no per-device or per-seat licenses. You're billed for the service, not per attempt, so you're never charged for a failed wipe.",
  },
];

const EraseFaq = () => {
  return (
    <section id="faq" className="relative bg-white font-body">
      <div className="mx-auto grid max-w-6xl gap-12 px-6 py-20 lg:grid-cols-[0.8fr_1.2fr] lg:py-28">
        {/* left intro */}
        <div className="lg:sticky lg:top-28 lg:self-start">
          <span className="inline-flex items-center gap-2 rounded-full border border-line bg-brand-bg px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.16em] text-brand-plum">
            FAQ
          </span>
          <h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-brand-plum sm:text-4xl">
            Erasure questions, answered
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-ink">
            Everything teams ask before trusting an automated, verified erasure pipeline on their refurb line.
          </p>
          <a
            href="#pilot"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-cta-gradient px-5 py-2.5 text-sm font-semibold text-white no-underline shadow-lg shadow-[#304BEF]/25 transition-transform duration-200 hover:-translate-y-0.5 hover:no-underline"
          >
            Still have questions? Join the pilot
          </a>
        </div>

        {/* accordion */}
        <Accordion
          type="single"
          collapsible
          defaultValue="item-1"
          className="w-full overflow-hidden rounded-2xl border border-line bg-white"
        >
          {QUESTIONS.map((item) => (
            <AccordionItem
              key={item.id}
              value={item.id}
              className="border-line px-5 last:border-b-0"
            >
              <AccordionTrigger className="text-[15px] font-semibold text-brand-plum">
                {item.title}
              </AccordionTrigger>
              <AccordionContent className="text-[15px] leading-relaxed text-muted">
                {item.content}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export default EraseFaq;
